import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import * as dto from 'client/dto';

import diseaseActivityDetailsSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityDetails.selectors';
import diseaseActivityListSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityList.selectors';
import diseaseActivitiesTimeFrameSelectors from 'diseaseActivitiesTimeFrame/diseaseActivitiesTimeFrame.selectors';
import { MapLayersState } from 'diseaseActivityPanels/disacPanelUtils';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import locationsSelectors from 'locations/locations.selectors';

import aoiLayer from './aoiLayer';
import eventDetails from './eventDetails';
import eventsLayer from './eventsLayer';

export const updateMapLayerOpacities = createAction('map/updateMapLayerOpacities');
export const updateAoiLayer = createAction('map/updateAoiLayer');
export const updateEventsLayerVisibility = createAction('map/updateEventsLayerVisibility');
export const updateEventsLayerData = createAction<dto.EventsPinModel[]>(
  'map/updateEventsLayerData'
);
export const updateOutbreakAndAirportLayer = createAction('map/updateOutbreakLayer');
export const zoomToLocation = createAction('map/zoomToLocation');

export function* updateAoiLayerSaga() {
  const mapState: MapLayersState = yield select(diseaseActivityPanelsSelectors.getMapState);
  const aoiIds: number[] = yield select(locationsSelectors.getAllLocationIds);
  const selectedLocationId: number = yield select(
    diseaseActivityPanelsSelectors.getSelectedLocationId
  );
  const { aoiLayerMode } = mapState;

  if (aoiLayerMode === 'all' && aoiIds) {
    yield call(
      aoiLayer.renderAois,
      aoiIds.map(id => ({ geonameId: id }))
    ); // TODO: collapse structure
  } else if (aoiLayerMode === 'selected' && selectedLocationId) {
    yield call(aoiLayer.renderAois, [{ geonameId: selectedLocationId }]);
  } else {
    yield call(aoiLayer.renderAois, []);
  }
  yield put(updateMapLayerOpacities());
}

export function* updateEventsLayerVisibilitySaga() {
  const mapState: MapLayersState = yield select(diseaseActivityPanelsSelectors.getMapState);
  const { eventsLayerVisibility } = mapState;

  if (eventsLayerVisibility === 'visible') yield call(eventsLayer.show);
  else yield call(eventsLayer.hide);
}

export function* updateEventsLayerDataSaga(action: PayloadAction<dto.EventsPinModel[]>) {
  const startDate: string = yield select(diseaseActivitiesTimeFrameSelectors.getStartDate);
  const selectedLocationId: number = yield select(
    diseaseActivityPanelsSelectors.getSelectedLocationId
  );

  if (selectedLocationId != null)
    yield call(eventsLayer.updateEventView, action.payload, startDate, selectedLocationId);
}

export function* updateOutbreakAndAirportLayerSaga() {
  const mapState: MapLayersState = yield select(diseaseActivityPanelsSelectors.getMapState);
  const allOutbreakLocations: dto.EventLocationModel[] = yield select(
    diseaseActivityListSelectors.getAllEventLocationsFlattened
  );
  const selectedEventLocations: dto.EventLocationModel[] = yield select(
    diseaseActivityDetailsSelectors.getSelectedEventLocations
  );
  const selectedEventAirports: dto.EventAirportModel = yield select(
    diseaseActivityDetailsSelectors.getSelectedEventAirports
  );
  const { outbreakLayerMode, airpoirtLayerVisibility } = mapState;

  if (outbreakLayerMode === 'all' && allOutbreakLocations) {
    yield call(eventDetails.showOutbreakLocations, allOutbreakLocations);
  } else if (outbreakLayerMode === 'selected' && selectedEventLocations) {
    yield call(eventDetails.showOutbreakLocations, selectedEventLocations);
  } else {
    yield call(eventDetails.hide);
  }

  if (airpoirtLayerVisibility === 'visible' && selectedEventAirports) {
    yield call(eventDetails.showAirportPins, selectedEventAirports);
  } else {
    yield call(eventDetails.showAirportPins, null);
  }
  yield put(updateMapLayerOpacities());
}

export function* updateMapLayerOpacitiesSaga() {
  const mapState: MapLayersState = yield select(diseaseActivityPanelsSelectors.getMapState);
  const { aoiLayerFade, outbreakLayerFade } = mapState;

  yield call(aoiLayer.setOpacityLevel, aoiLayerFade);
  yield call(eventDetails.setOpacityLevel, outbreakLayerFade);
}

export function* zoomToLocationSaga() {
  yield call(eventDetails.setExtentToEventDetail);
}

function* mapSaga() {
  yield takeLatest(updateAoiLayer, updateAoiLayerSaga);
  yield takeLatest(updateEventsLayerVisibility, updateEventsLayerVisibilitySaga);
  yield takeLatest(updateEventsLayerData, updateEventsLayerDataSaga);
  yield takeLatest(updateOutbreakAndAirportLayer, updateOutbreakAndAirportLayerSaga);
  yield takeLatest(updateMapLayerOpacities, updateMapLayerOpacitiesSaga);
  yield takeLatest(zoomToLocation, zoomToLocationSaga);
}

export default mapSaga;
